import React, { useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { IconAlertTriangleFilled, IconUserMinus } from '@tabler/icons-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  Tooltip,
  Typography,
  useBreakpoints,
  useMediaQuery,
} from '@cfa/react-components';

import { useInvalidateCertificateQueries } from '@/hooks/useInvalidateCertificateQueries';

import { formatDate } from '@/utils/date';
import { getProgressBarText } from '@/utils/generateProgressBarText';
import { generateProgressText } from '@/utils/generateProgressText';
import { invalidateAllPlanQueries } from '@/utils/queryInvalidation';

import PlanCardSubHeader from '@/components/Cards/PlanCard/PlanCardComponents/PlanCardSubHeader';
import PopoverMenuButton from '@/components/PopoverMenuButton/PopoverMenuButton';
import PopoverMenuButtonItem from '@/components/PopoverMenuButton/PopoverMenuButtonItem';
import ConfirmationModal from '@/components/popups/ConfirmationModal';
import DueDatePopUp from '@/components/popups/DueDatePopUpV2';
import ProgressBar from '@/components/ProgressBar/ProgressBar';
import SuccessToast from '@/components/Toasts/SuccessToast';

import { Badge, BasePlanVariant } from './components';
import ResetProgressModal from './components/ResetProgressModal';

import {
  resetPlanSteps,
  trainTogetherMarkPlanAsComplete,
  updateDueDate,
} from '@/_generated/api-clients/xp';

interface TeamMemberCardProps {
  completedStepCount: number;
  completionDate?: string | null;
  expirationDate?: string;
  finalScore?: number;
  id: string;
  isComplete: boolean;
  isLearnUpon?: boolean;
  isCompliance?: boolean;
  isFailed?: boolean;
  isFoodSafety?: boolean;
  isReports?: boolean;
  isReportsPlansView?: boolean;
  name: string;
  onUpdateDueDate?: (id: string, name: string) => void;
  onUnassign?: (id: string, name: string) => void;
  percentComplete?: number;
  planId?: string;
  planName?: string;
  previouslyCompletedPlanAssignmentDate?: string;
  selectedDueDate?: number;
  startDate?: string;
  stepCount: number;
  timeSpentOnPlan?: string | null;
  simpleUserName: string;
  userCanMarkAsComplete?: boolean;
  userAllowedToUpdateTMDueDateOnPlan?: boolean;
  complianceDueDateOffset?: number;
}

const TeamMemberCard: React.FC<TeamMemberCardProps> = ({
  completedStepCount,
  completionDate,
  expirationDate,
  finalScore,
  id,
  isComplete,
  isLearnUpon,
  isCompliance,
  isFailed,
  isFoodSafety,
  isReports,
  isReportsPlansView,
  name,
  onUnassign,
  onUpdateDueDate,
  percentComplete,
  planId,
  planName,
  previouslyCompletedPlanAssignmentDate,
  selectedDueDate,
  startDate,
  stepCount,
  timeSpentOnPlan,
  simpleUserName,
  userCanMarkAsComplete = true,
  userAllowedToUpdateTMDueDateOnPlan,
  complianceDueDateOffset,
}) => {
  const invalidateCertificates = useInvalidateCertificateQueries();
  const { reassignPlansEpic: reassignPlansEpicFF } = useFlags();
  const { t } = useTranslation();

  const [showMarkCompletedPopup, setShowMarkCompletedPopup] = useState(false);
  const [isDueDatePopUpOpen, setIsDueDatePopUpOpen] = useState(false);

  const [showRestartTrainingPopup, setShowRestartTrainingPopup] =
    useState(false);
  const breakpoints = useBreakpoints();
  const isSmAndDown = useMediaQuery(breakpoints.down('sm'));

  const handleUnassign = () => {
    onUnassign?.(id, name);
  };

  const handleUpdateDueDate = () => {
    onUpdateDueDate?.(id, name);
  };

  const handleMarkComplete = () => {
    setShowMarkCompletedPopup(true);
  };

  const onMarkCompleteCancel = () => {
    setShowMarkCompletedPopup(false);
  };

  const onMarkCompleteConfirm = () => {
    if (!planId) {
      datadogRum.addError(new Error('[TeamMemberCard] Plan ID is required'));
      return;
    }

    setShowMarkCompletedPopup(false);
    trainTogetherMarkPlanAsComplete({
      path: { planId },
      body: { userIds: [id] },
    })
      .then(async () => {
        await Promise.all([
          invalidateCertificates({ planId, userId: id }),
          invalidateAllPlanQueries(planId),
        ]);
      })
      .catch((err: unknown) => {
        datadogRum.addError(err);
      });
  };

  const handleRestartTraining = () => {
    if (reassignPlansEpicFF && isCompliance && complianceDueDateOffset) {
      const withOffset = new Date();
      withOffset.setDate(withOffset.getDate() + complianceDueDateOffset);

      handleReassignConfirm(withOffset.toISOString());
    } else if (reassignPlansEpicFF) {
      setIsDueDatePopUpOpen(true);
    } else setShowRestartTrainingPopup(true);
  };

  const onRestartPlanConfirm = () => {
    if (!planId) {
      datadogRum.addError(new Error('[TeamMemberCard] Plan ID is required'));
      return;
    }

    setShowRestartTrainingPopup(false);
    resetPlanSteps({
      path: { planId },
      body: { userIds: [id] },
    })
      .then(async () => {
        await Promise.all([
          invalidateCertificates({ planId, userId: id }),
          invalidateAllPlanQueries(planId),
        ]);
      })
      .catch((err: unknown) => {
        datadogRum.addError(err);
      });
  };

  const handleReassignConfirm = async (dueDate: string | undefined) => {
    if (!planId) {
      datadogRum.addError(new Error('[TeamMemberCard] Plan ID is required'));
      return;
    }
    setIsDueDatePopUpOpen(false);

    const resetPlanResult = await resetPlanSteps({
      path: { planId },
      body: { userIds: [id] },
    });
    const updateDueDateResult = await updateDueDate({
      path: { planId },
      body: {
        userIds: [id],
        // null has to be passed here to clear the due date
        // but the dueDate type is string | undefined
        dueDate: dueDate ?? (null as unknown as undefined),
      },
    });

    if (!resetPlanResult.error && !updateDueDateResult.error) {
      toast.custom(toastObj => (
        <SuccessToast id={toastObj.id} severity="success">
          {t('TrainingPlans.toastMessage.assignedTeamMember')}
        </SuccessToast>
      ));
      await Promise.all([
        invalidateCertificates({ planId, userId: id }),
        invalidateAllPlanQueries(planId),
      ]);
    }

    if (resetPlanResult.error || updateDueDateResult.error) {
      datadogRum.addError(
        new Error(
          `Failed to reassign plan:\nReset Plan Error: ${resetPlanResult.error?.message ?? 'None'}\nUpdate Due Date Error: ${updateDueDateResult.error?.message ?? 'None'}`,
        ),
      );
    }
  };

  const onRestartPlanCancel = () => {
    setShowRestartTrainingPopup(false);
  };

  return (
    <>
      {isLearnUpon ? (
        <Card className="relative h-auto animate-[fadeIn_linear_0.3s] flex-row overflow-visible p-0!">
          <CardContent className="flex w-full flex-row items-center justify-between p-4!">
            <Tooltip
              content={t('Reports.compliancePlanTooltip')}
              placement="bottom"
              showOnElementDelay={{
                showDelay: (!isSmAndDown && 500) as number,
              }}
            >
              <div
                className="mx-4 flex max-w-full grow flex-col items-start! justify-start overflow-hidden"
                data-simpleusername={simpleUserName}
              >
                <Typography
                  data-testid="PlanName"
                  fontWeight="bold"
                  variant="body1"
                  className="line-clamp-2 max-w-full min-w-0 overflow-hidden text-left! text-ellipsis"
                >
                  {name}
                </Typography>
                {isComplete && !!finalScore && (
                  <PlanCardSubHeader
                    className="success"
                    data-testid="PlanTaskCount"
                  >
                    {generateProgressText({
                      finalScore: finalScore.toString(),
                      isCompliance: isLearnUpon,
                      isComplete,
                      isFailed,
                      isFoodSafety,
                      timeSpentOnPlan: timeSpentOnPlan,
                    })}
                  </PlanCardSubHeader>
                )}
                {isComplete && !finalScore && (
                  <PlanCardSubHeader
                    className="success"
                    data-testid="PlanTaskCount"
                  >
                    {generateProgressText({
                      isCompliance: isLearnUpon,
                      isComplete,
                      isFailed,
                      isFoodSafety,
                      timeSpentOnPlan: timeSpentOnPlan,
                    })}
                  </PlanCardSubHeader>
                )}
                {isFailed && (
                  <PlanCardSubHeader
                    className="bold"
                    data-testid="PlanTaskCount"
                  >
                    {generateProgressText({
                      completedStepCount,
                      finalScore: finalScore?.toString(),
                      isCompliance: isLearnUpon,
                      isComplete,
                      isFailed,
                      isFoodSafety,
                      timeSpentOnPlan: timeSpentOnPlan,
                      stepCount,
                    })}
                  </PlanCardSubHeader>
                )}
                {!isComplete && !isFailed && (
                  <ProgressBar
                    current={percentComplete ?? 0}
                    isComplete={isComplete}
                    text={getProgressBarText({
                      isReports,
                      isComplete,
                      completedStepCount,
                      stepCount,
                      timeSpentOnPlan: timeSpentOnPlan,
                      isCompliance: isLearnUpon,
                      percentComplete,
                    })}
                    total={100}
                  />
                )}
                {isComplete && (
                  <PlanCardSubHeader className="bold">
                    {expirationDate ? (
                      t('TrainingPlans.certificationExpires', {
                        expirationDate: formatDate(expirationDate),
                      })
                    ) : (
                      <>
                        <IconAlertTriangleFilled className="text-feedback-warning-base! size-4!" />
                        <span className="ml-1">
                          {t('TrainingPlans.noExpirationDate')}
                        </span>
                      </>
                    )}
                  </PlanCardSubHeader>
                )}
              </div>
            </Tooltip>
            <Badge
              isComplete={isComplete}
              completionDate={completionDate}
              selectedDueDate={selectedDueDate ?? 0}
              showDate
            />
            <PopoverMenuButton hideMenu={isReportsPlansView}>
              <PopoverMenuButtonItem
                icon={<IconUserMinus />}
                onClick={handleUnassign}
                text={t('Button.unassign')}
              />
            </PopoverMenuButton>
          </CardContent>
        </Card>
      ) : (
        <BasePlanVariant
          id={id}
          planId={planId}
          name={name}
          simpleUserName={simpleUserName}
          previouslyCompletedPlanAssignmentDate={
            previouslyCompletedPlanAssignmentDate
          }
          isComplete={isComplete}
          isReports={isReports}
          isReportsPlansView={isReportsPlansView}
          completedStepCount={completedStepCount}
          completionDate={completionDate}
          expirationDate={expirationDate}
          isFailed={isFailed}
          isFoodSafety={isFoodSafety}
          percentComplete={percentComplete}
          selectedDueDate={selectedDueDate}
          startDate={startDate}
          stepCount={stepCount}
          timeSpentOnPlan={timeSpentOnPlan}
          handleUnassign={handleUnassign}
          handleUpdateDueDate={handleUpdateDueDate}
          handleMarkComplete={handleMarkComplete}
          handleRestartTraining={handleRestartTraining}
          userAllowedToUpdateTMDueDateOnPlan={
            userAllowedToUpdateTMDueDateOnPlan
          }
          userCanMarkAsComplete={userCanMarkAsComplete}
        />
      )}
      <ConfirmationModal
        bodyText={`${t('TrainingPlans.markCompleted1')} <b>${planName}</b> ${t(
          'TrainingPlans.markCompleted2',
        )}`}
        headerText={t('Generic.markCompleted')}
        isOpen={showMarkCompletedPopup}
        onClose={onMarkCompleteCancel}
        primaryButtonHandler={onMarkCompleteConfirm}
        primaryButtonText={t('Button.yes')}
        secondaryButtonHandler={onMarkCompleteCancel}
        secondaryButtonText={t('Button.cancel')}
      />

      <ConfirmationModal
        bodyText={`${t('TrainingPlans.restartPlan1')} <b>${planName}</b> ${t(
          'TrainingPlans.restartPlan2',
        )}`}
        headerText={t('Generic.restartTraining')}
        isOpen={showRestartTrainingPopup}
        onClose={onRestartPlanCancel}
        primaryButtonHandler={onRestartPlanConfirm}
        primaryButtonText={t('Button.yes')}
        secondaryButtonHandler={onRestartPlanCancel}
        secondaryButtonText={t('Button.cancel')}
      />
      <DueDatePopUp
        isOpen={isDueDatePopUpOpen}
        onOpenChange={setIsDueDatePopUpOpen}
        onSave={handleReassignConfirm}
      />
      <ResetProgressModal
        teamMemberId={id}
        teamMemberName={name}
        planId={planId}
        planName={planName}
      />
    </>
  );
};

export default TeamMemberCard;
